import { useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { dictionaries, type DictKey, fi as fiDict } from "./dictionaries";
import { langForLocale, setShopifyContext, type ShopifyCountry } from "@/lib/shopify";

type DictId = keyof typeof dictionaries; // "fi" | "en" | "sv" | "et" | "en-US"

export const LOCALES = ["fi", "en", "sv", "et"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "fi";
const STORAGE_KEY = "m24.lang";

/** Markets are commercial territories (currency + shipping + tax). */
export const MARKETS = ["fi", "us"] as const;
export type Market = (typeof MARKETS)[number];
export const DEFAULT_MARKET: Market = "fi";

export function isMarket(value: unknown): value is Market {
  return typeof value === "string" && (MARKETS as readonly string[]).includes(value);
}

export function marketFromSearch(search: string | undefined | null): Market {
  if (!search) return DEFAULT_MARKET;
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const m = params.get("market");
  return isMarket(m) ? m : DEFAULT_MARKET;
}

/** The `/us` route (and any `/us/*` child) is the US market landing, so
 *  treat it as market=us even without an explicit `?market=us` param.
 *  This keeps header/footer/cart/Shopify context in sync with the page. */
export function marketFromPathname(pathname: string | undefined | null): Market | null {
  if (!pathname) return null;
  if (pathname === "/us" || pathname.startsWith("/us/")) return "us";
  return null;
}

export function useMarket(): Market {
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return marketFromPathname(pathname) ?? marketFromSearch(search);
}

/** Shopify CountryCode for a market. */
export function countryForMarket(m: Market): ShopifyCountry {
  return m === "us" ? "US" : "FI";
}

/** Currency label shown before Shopify responds. */
export function currencyForMarket(m: Market): "EUR" | "USD" {
  return m === "us" ? "USD" : "EUR";
}

/**
 * When market=us we force English UI regardless of the ?lang= param
 * (US shoppers get English + USD; the `lang` param is only meaningful
 * for FI-market translations).
 */
export function effectiveLocaleFor(market: Market, urlLocale: Locale): Locale {
  return market === "us" ? "en" : urlLocale;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** Read locale from a URL search string (e.g. "?lang=en"). */
export function localeFromSearch(search: string | undefined | null): Locale {
  if (!search) return DEFAULT_LOCALE;
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const lang = params.get("lang");
  return isLocale(lang) ? lang : DEFAULT_LOCALE;
}

/** Translate a key for a specific dictionary id, with fallbacks (en-US → en → fi). */
export function tFor(id: DictId, key: DictKey, vars?: Record<string, string | number>): string {
  const primary = dictionaries[id] as Partial<Record<DictKey, string>>;
  const enFallback = dictionaries.en as Partial<Record<DictKey, string>>;
  const raw =
    primary?.[key] ??
    (id === "en-US" ? enFallback?.[key] : undefined) ??
    fiDict[key] ??
    key;
  if (!vars) return raw;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
    raw,
  );
}

/** Get the current locale from URL, respecting the active market. */
export function useLocale(): Locale {
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const market = marketFromPathname(pathname) ?? marketFromSearch(search);
  return effectiveLocaleFor(market, localeFromSearch(search));
}

/** Translate function bound to the current locale. */
export function useT() {
  const locale = useLocale();
  const market = useMarket();
  const id: DictId = market === "us" ? "en-US" : locale;
  return useCallback(
    (key: DictKey, vars?: Record<string, string | number>) => tFor(id, key, vars),
    [id],
  );
}

/**
 * Build a new search-param object that preserves current params but switches lang.
 * Pass to <Link search={...}> or navigate({ search: ... }).
 */
export function switchLangSearch<T extends Record<string, unknown>>(prev: T, target: Locale): T {
  const next = { ...prev } as Record<string, unknown>;
  if (target === DEFAULT_LOCALE) delete next.lang;
  else next.lang = target;
  return next as T;
}

/**
 * Maps our locales to Shopify Storefront API LanguageCode values.
 *
 * Note: fi/en/et all resolve to the Shopify primary locale ("EN" slot),
 * which holds the curated Finnish catalogue copy. Asking for language "FI"
 * would return the stale supplier translation layer and visibly swap
 * product titles/blurbs right after hydration.
 */
export function shopifyLanguageCode(locale: Locale): "FI" | "EN" | "SV" | "ET" {
  return langForLocale(locale) as "FI" | "EN" | "SV" | "ET";
}

/** Sync <html lang> + Shopify context + localStorage with the current market/locale. */
export function useLocaleEffect() {
  const locale = useLocale();
  const market = useMarket();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = market === "us" ? "en-US" : locale;
    setShopifyContext({
      country: countryForMarket(market),
      language: shopifyLanguageCode(locale),
    });
    // Shopify returns localized product content via @inContext. Because the
    // fetch reads the module-scoped language, we must invalidate cached
    // product queries so switching ?lang=… refetches titles/descriptions
    // in the newly selected language (product cards, PDP, related, cart
    // line items sync via CartDrawer's syncCart already).
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["product"] });
    queryClient.invalidateQueries({ queryKey: ["related"] });
    try {
      localStorage.setItem(STORAGE_KEY, market === "us" ? "us" : locale);
    } catch {
      /* ignore */
    }
  }, [locale, market, queryClient]);
}

/** On first visit, if URL has no ?lang= but localStorage has a saved preference, restore it. */
export function useRestoreLocaleOnce() {
  useEffect(() => {
    // Intentionally a no-op: the site must always load in Finnish by default.
    // Users can switch language via the LanguageSwitcher (?lang=...) within a session,
    // but we never auto-restore a previously chosen non-default locale on landing.
    if (typeof window === "undefined") return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);
}